import React, {useContext, useEffect, useState} from "react";
import {Box, Button, FormControlLabel, Grid, IconButton, Paper, Radio, Stack, Typography, useTheme} from "@mui/material";
import {EditRounded, SaveRounded} from "@mui/icons-material";
import {useForm, FormProvider} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import SectionHeader from "../../../../../util/component/SectionHeader";
import ContentBody from "../../../common/content-body/ContentBody";
import {IGeneralDataActivation} from "../../models";
import {FormAutocomplete, FormRadioGroup, FormTextField } from "../../../common/form-fields-wrappers";
import {getGeneralDataActivation, saveBookabilityVisibility} from "../../dmsDetail.actions";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useHasScope} from "../../../../../config/globalConfig";
import { NetworkScope } from "../../../types";

const mockDmsType = [
    'Real',
    'Shuttle',
    'Dealer',
    'Temporary',
    'Implant c/o company',
    'DMS self public area',
    'DMS private area',
    'White label',
    'Meet & Greet/ Virtual',
    'Site of interest'
];

const ActivationSetupPanel = () => {
    const hasScope = useHasScope;
    const {t} = useTranslation(["network", "translation"]);
    const theme = useTheme();
    const navigate = useNavigate();
    const location: any = useLocation();
    const [isDisable, setIsDisable] = useState(true);
    const [activationObj, setActivationObj] = useState<IGeneralDataActivation>();
    const { dispatchGlobalData } = useContext(GlobalContext);

    const generalDataForm = useForm({
        defaultValues: {
            currentDate: activationObj?.generalData?.currentDate ? dayjs(activationObj?.generalData?.currentDate).format("DD-MM-YYYY") : "",
            dmsName: activationObj?.generalData?.dmsName ? activationObj?.generalData?.dmsName : "",
            identificationCode: activationObj?.generalData?.identificationCode ? activationObj?.generalData?.identificationCode : "",
            dmsType: activationObj?.generalData?.dmsType ? activationObj?.generalData?.dmsType : "",
            afterHours: activationObj?.generalData?.afterHours == true ? "Yes" : "No",
            currentStatus: activationObj?.generalData?.currentStatus ? activationObj?.generalData?.currentStatus : "",
            currentAttribute: activationObj?.generalData?.currentAttribute ? activationObj?.generalData?.currentAttribute : "",
            lastModificationDate: activationObj?.generalData?.lastModificationDate ? dayjs(activationObj?.generalData?.lastModificationDate).format("DD-MM-YYYY HH:mm") : "",
            lastModificationMadeBy: activationObj?.generalData?.lastModificationMadeBy ? activationObj?.generalData?.lastModificationMadeBy : "",
        }
    })

    const workRestForm = useForm({
        defaultValues: {bookAbility: activationObj?.bookAbility === true ? "Yes" : "No"},
    });

    const onSubmitWorkRest = (data: any) => {
        const reqParams = {
            id: location.state.msId,
            bookAbility:  data.bookAbility === "Yes"
        }
        saveBookabilityVisibility(reqParams)
            .then((res: any) =>{
                setIsDisable(true);
                workRestForm.setFocus("bookAbility");
                res.bookAbility === true ? workRestForm.setValue("bookAbility", "Yes") : workRestForm.setValue("bookAbility", "No");
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            });

    }

    const handleBackStep = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        let path = `/mobility-store/dms-detail/${location.state.msId}`;
        navigate(path, {state: {msId: location.state.msId}});
    }

    useEffect(() => {
        const reqParameters = { id: location.state.msId }
        getGeneralDataActivation(reqParameters)
            .then((res:any) => {
                setActivationObj(res.generalData);
                generalDataForm.setValue("currentDate", dayjs(res.generalData.currentDate).format("DD-MM-YYYY"));
                generalDataForm.setValue("dmsName",res.generalData.dmsName);
                generalDataForm.setValue("identificationCode",res.generalData.identificationCode);
                generalDataForm.setValue("dmsType",res.generalData.dmsType);
                res.generalData.afterHours === true ? generalDataForm.setValue("afterHours", "Yes") : generalDataForm.setValue("afterHours", "No");
                generalDataForm.setValue("currentStatus",res.generalData.currentStatus);
                generalDataForm.setValue("currentAttribute",res.generalData.currentAttribute);
                generalDataForm.setValue("lastModificationDate",dayjs(res.generalData.lastModificationDate).format("DD-MM-YYYY HH:mm"));
                generalDataForm.setValue("lastModificationMadeBy",res.generalData.lastModificationMadeBy);

                res.bookAbility === true ? workRestForm.setValue("bookAbility", "Yes") : workRestForm.setValue("bookAbility", "No");
            });
    }, []);

    return(
        <>
            <SectionHeader title={t("dmsDetail.activationSetupPanel.title")}></SectionHeader>
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Typography variant="h6">{t("dmsDetail.activationSetupPanel.form.label.general")}</Typography>
                    <FormProvider {...generalDataForm}>
                        <Grid container direction="row" alignItems="start">
                            <Grid container item direction="row" xs={12} columnSpacing={2}>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="currentDate"
                                        label={t("dmsDetail.activationSetupPanel.form.label.currentDate")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="dmsName"
                                        label={t("dmsDetail.activationSetupPanel.form.label.dmsName")}
                                    />

                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="identificationCode"
                                        label={t("dmsDetail.activationSetupPanel.form.label.identificationCode")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormAutocomplete
                                        disabled
                                        control={generalDataForm.control}
                                        name="dmsType"
                                        options={mockDmsType}
                                        label={t("dmsDetail.activationSetupPanel.form.label.dmsType")}
                                        getOptionLabel={(option) => option ? option : ''}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="currentStatus"
                                        label={t("dmsDetail.activationSetupPanel.form.label.currentStatus")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="currentAttribute"
                                        label={t("dmsDetail.activationSetupPanel.form.label.currentAttribute")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="lastModificationDate"
                                        label={t("dmsDetail.activationSetupPanel.form.label.lastModificationDate")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormTextField
                                        disabled
                                        control={generalDataForm.control}
                                        name="lastModificationMadeBy"
                                        label={t("dmsDetail.activationSetupPanel.form.label.lastModificationMadeBy")}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormRadioGroup
                                        control={generalDataForm.control}
                                        name="afterHours"
                                        label={t("dmsDetail.activationSetupPanel.form.label.afterHours")}
                                    >
                                        <FormControlLabel disabled value="Yes" control={<Radio/>} label={t("dmsDetail.activationSetupPanel.form.label.yes")} />
                                        <FormControlLabel disabled value="No" control={<Radio/>} label={t("dmsDetail.activationSetupPanel.form.label.no")} />
                                    </FormRadioGroup>
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormProvider>

                    <FormProvider {...workRestForm}>
                        { hasScope(NetworkScope.MsMng) &&
                            <Stack direction="row" spacing={2} justifyContent="end" alignItems="end">
                                {isDisable ?
                                    <IconButton color="accent" onClick={() => setIsDisable(false)}>
                                        <EditRounded/>
                                    </IconButton> :
                                    <IconButton color="accent" onClick={(workRestForm.handleSubmit(onSubmitWorkRest))}>
                                        <SaveRounded/>
                                    </IconButton>}
                            </Stack>
                        }
                        <Box py={3}>
                            <FormRadioGroup
                                disabled={isDisable}
                                control={workRestForm.control}
                                name="bookAbility"
                                label={t("dmsDetail.activationSetupPanel.form.label.bookAbility")}>
                                <FormControlLabel  value="Yes" control={<Radio />} label={t("dmsDetail.activationSetupPanel.form.label.yes")} />
                                <FormControlLabel  value="No" control={<Radio />} label={t("dmsDetail.activationSetupPanel.form.label.no")} />
                            </FormRadioGroup>
                        </Box>
                    </FormProvider>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: theme.spacing(2), mb: theme.spacing(2)}}>
                        <Button variant='text' color="accent" onClick={handleBackStep}>
                            {t("dmsDetail.activationSetupPanel.button.back")}
                        </Button>
                    </Box>
                </Paper>
            </ContentBody>
        </>
    )
}

export default ActivationSetupPanel;
